import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Helper } from '@react-three/drei'

// One live spotlight on an orbit rig — the light sits at cylindrical
// (distance, height, yaw) about the Y axis and, by default, aims at a target
// at world [0, targetHeight, 0]. Since ENG-1190 SceneLights mounts one per
// `spot` entry in liveTuneStore.lights, and the rotate gizmo can aim it FREELY:
// an `aimX/aimY/aimZ` Euler on the entry (set by createLightAdapter) points the
// beam along that rotation's forward instead of at the axis target.
//
// `tune` is one spotlight slice (liveTuneStore's `spotlight` / `spotlight2` /
// `spotlight3`). `lightGlobal`/`targetGlobal` are the FULL window property
// names (e.g. '__SPOTLIGHT__') this instance publishes itself under. Slot 1
// MUST be called with '__SPOTLIGHT__' / '__SPOTLIGHT_TARGET__' — those are
// locked by tests/e2e/ci/spotlight.spec.js and predate this component. Slots
// 2/3 use their own additive names (never a rename of the existing one, per
// CLAUDE.md's window.__* rule).
// URL OVERRIDES, READ HERE RATHER THAN THROUGH THE TUNE STORE — because the
// store cannot be relied on to carry them. `readInitial()` IGNORES the URL
// whenever localStorage holds a stored tune ("stored value wins"), and an
// environment's own `default_tune` is applied asynchronously after boot on top
// of whatever was there. So `?spotshadows=0` routed through the store parsed
// correctly and then silently did nothing — measured: 316.7 ms, three casters
// still live. A performance escape hatch that quietly fails is worse than none,
// so these are applied at the light itself, where nothing can outrank them.
//
//   ?spotshadows=0   no spotlight CASTS a shadow — the big one: measured, three
//                    casters at 4096 cost ~14x a frame against casting off
//   ?shadowmap=N     force the map resolution instead (1024 is ~2x rather than 14x)
//
// Figures and hardware in docs/OGS_ARCHITECTURE.md. A light's own `castShadow`
// is the per-light route and works normally; this is only the override.
const URL_SPOT = (() => {
  try {
    const p = new URLSearchParams(window.location.search)
    const off = p.has('spotshadows') && p.get('spotshadows') === '0'
    const raw = p.get('shadowmap')
    const n = raw == null ? NaN : parseInt(raw, 10)
    const size = (Number.isFinite(n) && n >= 256 && n <= 4096 && (n & (n - 1)) === 0) ? n : null
    if (off || size) console.info(`[spotlight] URL override: ${off ? 'shadows OFF' : ''}`
      + `${size ? ` shadow map ${size}` : ''}`)
    return { off, size }
  } catch { return { off: false, size: null } }
})()

// `tune` is one light entry. `id` is its key in the lights map; the light and
// its target publish under window.__LIGHTS__[id]. `lightGlobal`/`targetGlobal`
// are the FULL legacy window property names the fixed slots used to publish
// under (`spot1` MUST be '__SPOTLIGHT__' / '__SPOTLIGHT_TARGET__' — locked by
// tests/e2e/ci/spotlight.spec.js and predating this component; spot2/3 have
// their _2/_3 variants). A light with no legacy name publishes only under
// __LIGHTS__.
//
// `selected` draws the cone helper (Unreal shows a selected light's cone and
// direction); the entry's own `gizmo` flag keeps it drawn when not selected.
// `shadowMapSize` is the EFFECTIVE resolution — the authored `tune.shadowMapSize`
// after this viewer's scalability tier has capped it (shadowQuality.js). Passed
// in rather than read here so the authored value and the rendered one never get
// confused, and so this component stays a pure renderer of what it is handed.
// `?shadowmap=N` still wins over both: a typed flag is a decision.
export default function SpotlightRig({ tune, id, shadowMapSize: authoredMapSize = tune.shadowMapSize, lightGlobal, targetGlobal, selected = false, helperColor = '#00e5ff' }) {
  // `=== false` rather than a bare read: an entry persisted before `castShadow`
  // existed has no such key and must keep casting, as it always did.
  const castShadow = !URL_SPOT.off && tune.castShadow !== false
  const shadowMapSize = URL_SPOT.size ?? authoredMapSize
  const spotTarget = useMemo(() => new THREE.Object3D(), [])
  const spotRef = useRef(null)
  const prevMapSizeRef = useRef(null)

  // The light stays INSIDE a yawed group at local [0, height, distance], which
  // is what `__SPOTLIGHT__.position` has always reported and what
  // spotlight.spec.js reads. Placing it at its world point instead (and
  // dropping the group) renders identically — the world position is the same
  // either way — but silently redefines that published local coordinate, which
  // is a contract, not an implementation detail.
  const yaw = THREE.MathUtils.degToRad(tune.yaw)
  const d = Math.max(0, tune.distance)
  // The same point in WORLD space, for the aim below. Derived rather than read
  // off the object so it is available on the first render, before the group's
  // matrix exists.
  const world = [d * Math.sin(yaw), tune.height, d * Math.cos(yaw)]
  // Where the beam points: the free aim's forward (one metre out — the target
  // only sets a direction), else the axis target the rig aims at by default.
  const aimed = Number.isFinite(tune.aimX) && Number.isFinite(tune.aimY) && Number.isFinite(tune.aimZ)
  const aimPoint = aimed
    ? (() => {
      const f = new THREE.Vector3(0, 0, -1).applyEuler(new THREE.Euler(tune.aimX, tune.aimY, tune.aimZ, 'XYZ'))
      return [world[0] + f.x, world[1] + f.y, world[2] + f.z]
    })()
    : [0, tune.targetHeight, 0]

  // Published unconditionally (not gated on tune.enabled) — same rule as the
  // original Scene.jsx spotTarget: the target must never dangle, and a
  // disabled light still leaves its aim point inspectable.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (targetGlobal) window[targetGlobal] = spotTarget
    if (id) {
      window.__LIGHTS__ = window.__LIGHTS__ ?? {}
      window.__LIGHTS__[id] = {
        type: 'spot',
        get light() { return spotRef.current },
        target: spotTarget,
        // Live world position of the light, or null while off.
        pos: () => {
          const l = spotRef.current
          if (!l) return null
          const p = l.getWorldPosition(new THREE.Vector3())
          return [p.x, p.y, p.z]
        },
      }
    }
    return () => {
      if (targetGlobal && window[targetGlobal] === spotTarget) delete window[targetGlobal]
      if (id && window.__LIGHTS__?.[id]?.target === spotTarget) delete window.__LIGHTS__[id]
    }
  }, [spotTarget, targetGlobal, id])

  // Shadow-map resolution/bias applied imperatively: resizing shadowMapSize
  // needs the old render target(s) disposed or the map goes stale/black.
  useEffect(() => {
    const light = spotRef.current
    if (!light) return
    const size = shadowMapSize
    if (prevMapSizeRef.current !== null && prevMapSizeRef.current !== size) {
      light.shadow.mapSize.set(size, size)
      if (light.shadow.map) { light.shadow.map.dispose(); light.shadow.map = null }
      if (light.shadow.mapPass) { light.shadow.mapPass.dispose(); light.shadow.mapPass = null }
    }
    prevMapSizeRef.current = size
    light.shadow.bias = tune.shadowBias
  }, [tune.enabled, shadowMapSize, tune.shadowBias])

  if (!tune.enabled) return null

  return (
    <>
      <group rotation={[0, yaw, 0]}>
        <spotLight
          ref={(node) => {
            spotRef.current = node
            if (typeof window !== 'undefined' && lightGlobal) {
              if (node) window[lightGlobal] = node
              else delete window[lightGlobal]
            }
          }}
          position={[0, tune.height, d]}
          target={spotTarget}
          color={tune.color}
          intensity={tune.intensity}
          angle={THREE.MathUtils.degToRad(tune.angle)}
          penumbra={tune.penumbra}
          decay={2}
          distance={tune.range}
          // A shadow-casting spotlight is a WHOLE EXTRA RENDER of the scene per
          // frame plus two VSM blur passes over its own map — measured at 233 ms
          // a frame for three of these at 4096, against 16.7 ms with them off,
          // and it does not shrink with the window because the map has its own
          // resolution. `castShadow` defaults true so nothing changes for an
          // existing tune; `?spotshadows=0` and the panel's CAST SHADOWS toggle
          // are the ways out. `=== false` rather than a bare read, so a slice
          // persisted before this field existed is treated as casting.
          castShadow={castShadow}
          shadow-mapSize-width={shadowMapSize}
          shadow-mapSize-height={shadowMapSize}
          shadow-camera-near={0.5}
          shadow-camera-far={50}
          shadow-bias={tune.shadowBias}
          shadow-radius={tune.shadowRadius}
          shadow-blurSamples={tune.shadowBlurSamples}
        >
          {/* Cone gizmo — a SpotLightHelper attached to this light; updates
             each frame so it tracks the placement + aim live. Shown while the
             light is selected in the outliner, or always when `gizmo` is on. */}
          {(tune.gizmo || selected) && <Helper type={THREE.SpotLightHelper} args={[helperColor]} />}
        </spotLight>
      </group>
      {/* The aim point is WORLD space, so it sits outside the yawed group — the
         beam must not swing with the rig when it has been aimed freely. */}
      <primitive object={spotTarget} position={aimPoint} />
    </>
  )
}
